// src/pages/Loyalty.js — v2
// Uses PHONE NUMBER as the Firestore document ID (prevents duplicate records)

import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';
import { expiryDaysFrom, lotsFromDoc, activeLots, balanceOf, compactLots, nextExpiry, fmtDate } from '../utils/loyalty';

const tier = pts => pts>=1500?'Gold':pts>=500?'Silver':'Bronze';
const tierColor = t => ({Gold:'tag-amber',Silver:'tag-gray',Bronze:'tag-red'})[t];

export default function Loyalty() {
  const [members, setMembers] = useState([]);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState({ name:'', phone:'' });

  const [settings, setSettings] = useState({});

  useEffect(()=>onSnapshot(collection(db,'loyalty'),s=>setMembers(s.docs.map(d=>({id:d.id,...d.data()})))),[]);
  useEffect(()=>onSnapshot(doc(db,'config','settings'),s=>setSettings(s.exists()?s.data():{})),[]);

  // points shown here are only the ones that have not expired yet
  const days = expiryDaysFrom(settings);
  const rows = members.map(m => {
    const lots = lotsFromDoc(m);
    const live = activeLots(lots, days);
    return { ...m, active: balanceOf(live), exp: nextExpiry(lots, days), expired: balanceOf(lots) - balanceOf(live) };
  });

  const addMember=async()=>{
    if(!form.name||!form.phone){toast.error('Fill all fields');return;}
    const phone = form.phone.trim();
    const existing = await getDoc(doc(db,'loyalty',phone));
    if (existing.exists()) { toast.error('This phone number already has a loyalty account'); return; }
    await setDoc(doc(db,'loyalty',phone),{name:form.name,phone,points:0,lots:[],totalVisits:0,createdAt:serverTimestamp()});
    toast.success('Member added'); setModal(false); setForm({name:'',phone:''});
  };

  const addPoints=async(phone,amount)=>{
    const pts=Math.floor(amount/10);
    if (pts<=0) { toast.error('Amount is too small to earn a point'); return; }
    const m=members.find(x=>x.id===phone);
    const live=activeLots(lotsFromDoc(m), days);                       // expired points are dropped
    const lots=compactLots([...live,{pts,at:Date.now()}]);
    await setDoc(doc(db,'loyalty',phone),{points:balanceOf(lots),lots,lastEarnedAt:Date.now(),totalVisits:(m?.totalVisits||0)+1},{merge:true});
    toast.success(`+${pts} points`);
  };

  return (
    <div>
      <div className="grid4" style={{ marginBottom:16 }}>
        {[['Members',rows.length],['Gold',rows.filter(m=>tier(m.active)==='Gold').length],['Silver',rows.filter(m=>tier(m.active)==='Silver').length],['Bronze',rows.filter(m=>tier(m.active)==='Bronze').length]].map(([l,v])=>(
          <div key={l} className="metric"><div className="metric-label">{l}</div><div className="metric-val">{v}</div></div>
        ))}
      </div>
      <div className="card">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div className="sec-title" style={{ marginBottom:0 }}>Members</div>
          <button className="btn btn-primary" style={{ fontSize:12, padding:'5px 12px' }} onClick={()=>setModal(true)}>+ Add Member</button>
        </div>
        <div style={{ fontSize:12, color:'#888', marginBottom:10 }}>
          {days>0 ? <>Points expire <b>{days} days</b> after they are earned. Only unexpired points are shown. (Change this in Admin Panel → Settings.)</> : 'Points never expire.'}
        </div>
        <table className="table">
          <thead><tr><th>Name</th><th>Phone</th><th>Points</th><th>Expiring</th><th>Tier</th><th>Visits</th><th>Add Points</th></tr></thead>
          <tbody>
            {[...rows].sort((a,b)=>b.active-a.active).map(m=>(
              <tr key={m.id}>
                <td style={{ fontWeight:500 }}>{m.name}</td>
                <td style={{ color:'#888', fontSize:12 }}>{m.phone||m.id}</td>
                <td><b>{m.active}</b></td>
                <td style={{ fontSize:11, color:'#B3702A' }}>{m.exp ? `${m.exp.pts} pts · ${fmtDate(m.exp.at)}` : '—'}{m.expired>0 && <div style={{ color:'#aaa' }}>{m.expired} expired</div>}</td>
                <td><span className={`tag ${tierColor(tier(m.active))}`}>{tier(m.active)}</span></td>
                <td>{m.totalVisits}</td>
                <td>
                  <input type="number" placeholder="₹ spent" id={`p-${m.id}`}
                    style={{ width:80, border:'1px solid #ddd', borderRadius:6, padding:'3px 6px', fontSize:12 }} />
                  <button className="btn" style={{ fontSize:11, padding:'3px 9px', marginLeft:5 }}
                    onClick={()=>{ const v=document.getElementById(`p-${m.id}`).value; if(v) addPoints(m.id,+v); }}>Add</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Add Loyalty Member</h3>
            <div className="field"><label>Customer Name</label><input className="input" placeholder="e.g. Rohit Gupta" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} /></div>
            <div className="field"><label>Phone Number</label><input className="input" placeholder="10-digit mobile" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} /></div>
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <button className="btn" style={{ flex:1 }} onClick={()=>setModal(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={addMember}>Add Member</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}